/**
 * The handoff from a patient's chart to V3's grid: "schedule for this person".
 *
 * Its own file, with no imports, because it crosses a feature boundary — the patient
 * header builds it and V3 reads it. The header can take the type alone (`import type`,
 * erased at build) and never pull the scheduler into the chart's chunk.
 *
 * V3's form sheet is mounted by `<StatusHosts/>` on V3's pages only, so the chart cannot
 * open that form where it stands; it navigates and carries this instead.
 */
export interface SchedulingForRequest {
  patientId: number;
  /** The real name — it is seeded into the form's patient field. */
  patientLabel: string;
  /** Initials, for the bar while privacy mode is on. */
  privateLabel: string;
  clinicLabel: string | null;
  /** Where in the chart the user was, so the mode can put them back. */
  returnToPath: string;
  /**
   * The follow-up type the visit drawer suggests (ENG-2027): the first slot clicked opens
   * the form with the patient AND the type set.
   */
  appointmentTypeId?: number | null;
  /**
   * Open V3's Find Next Available once the mode has started (ENG-2027), filled with what
   * the chart entry resolved. Each entry has its own rule for clinic and provider (the
   * header wants an ACTIVE preferred provider and honours view-own-schedule; the visit
   * drawer uses the CURRENT clinic; the completion flow uses the completed visit's), so
   * V3 derives nothing here.
   */
  findNext?: SchedulingForFindNext;
}

export interface SchedulingForFindNext {
  clinicId: number | null;
  providerIds: number[];
  appointmentTypeId: number | null;
  startFromMonths: number | null;
}

/**
 * Initials for the bar under privacy mode, the way the patient header builds them. Here,
 * with no imports, so every chart entry spells them the same.
 */
export const schedulingForInitials = (
  patient: {
    first_name?: string | null;
    last_name?: string | null;
  } | null
): string =>
  [patient?.first_name?.[0], patient?.last_name?.[0]].filter(Boolean).join(" ");

/** The `location.state` key. One spelling, shared by the writer and the reader. */
export const SCHEDULING_FOR_STATE_KEY = "schedulingFor";
